import React from 'react';
import { Alert, Box, FormControl, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';

export interface GoalAmountFields {
  threshold?: string;
  min?: string;
  max?: string;
}

export interface GoalEditorChild {
  habitId: string;
  habitDisplayName: string;
}

interface ParentChildGoalEditorProps {
  childHabits: GoalEditorChild[];
  childGoalAmounts: Record<string, GoalAmountFields>;
  onChildGoalAmountChange: (habitId: string, patch: GoalAmountFields) => void;
  parentHabitDisplayName?: string;
  selectedGoalType: 'RANGE' | 'THRESHOLD' | 'AVERAGE';
  onSelectedGoalTypeChange: (goalType: 'RANGE' | 'THRESHOLD' | 'AVERAGE') => void;
  goalUnit: string;
  onGoalUnitChange: (goalUnit: string) => void;
  intervalUnit: string;
  onIntervalUnitChange: (intervalUnit: string) => void;
  thresholdValue: string;
  onThresholdValueChange: (value: string) => void;
  rangeMin: string;
  onRangeMinChange: (value: string) => void;
  rangeMax: string;
  onRangeMaxChange: (value: string) => void;
  availableGoalUnits: string[];
  availableIntervalUnits: string[];
  parentAmountsAsText: boolean;
  childValidationError?: string | null;
  goalTypeSelectorPlacement?: 'top' | 'bottom';
}

const inputRootSx = {
  bgcolor: 'rgba(0,0,0,0.2)',
  '& fieldset': { borderColor: 'rgba(50,202,29,0.5)' },
  '&:hover fieldset': { borderColor: 'rgba(50,202,29,0.7)' },
  '&.Mui-focused fieldset': { borderColor: '#32CA1D' },
};

const selectSx = {
  bgcolor: 'rgba(0, 0, 0, 0.2)',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(50, 202, 29, 0.5)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(50, 202, 29, 0.7)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#32CA1D',
  },
  color: '#ffffff',
};

const ParentChildGoalEditor: React.FC<ParentChildGoalEditorProps> = ({
  childHabits,
  childGoalAmounts,
  onChildGoalAmountChange,
  parentHabitDisplayName,
  selectedGoalType,
  onSelectedGoalTypeChange,
  goalUnit,
  onGoalUnitChange,
  intervalUnit,
  onIntervalUnitChange,
  thresholdValue,
  onThresholdValueChange,
  rangeMin,
  onRangeMinChange,
  rangeMax,
  onRangeMaxChange,
  availableGoalUnits,
  availableIntervalUnits,
  parentAmountsAsText,
  childValidationError,
  goalTypeSelectorPlacement = 'bottom',
}) => {
  const showIntervalUnitControls = selectedGoalType !== 'AVERAGE';

  const goalTypeSelector = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ color: '#32CA1D' }}><strong>Goal Type:</strong></Typography>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <Select
          value={selectedGoalType}
          onChange={(e) => onSelectedGoalTypeChange(e.target.value as 'RANGE' | 'THRESHOLD' | 'AVERAGE')}
          sx={selectSx}
        >
          <MenuItem value="THRESHOLD">Threshold</MenuItem>
          <MenuItem value="RANGE">Range</MenuItem>
          <MenuItem value="AVERAGE">Average</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  return (
    <>
      {goalTypeSelectorPlacement === 'top' && goalTypeSelector}

      {childHabits.length > 0 &&
        [...childHabits]
          .sort((a, b) => a.habitDisplayName.localeCompare(b.habitDisplayName))
          .map((child) => {
            const amounts = childGoalAmounts[child.habitId] || {};
            return (
              <Box key={child.habitId} sx={{ mb: 2, pb: 2, borderBottom: '1px solid rgba(50,202,29,0.15)' }}>
                <Typography variant="body2" sx={{ color: '#32CA1D', fontWeight: 700, mb: 1 }}>
                  {child.habitDisplayName} Goal
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ color: '#32CA1D' }}><strong>Amount:</strong></Typography>
                  {selectedGoalType === 'THRESHOLD' ? (
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{ step: '0.01', min: '0.01' }}
                      value={amounts.threshold ?? ''}
                      onChange={(e) => onChildGoalAmountChange(child.habitId, { threshold: e.target.value })}
                      sx={{
                        width: 100,
                        '& .MuiOutlinedInput-root': inputRootSx,
                        '& .MuiOutlinedInput-input': { color: '#ffffff' },
                      }}
                    />
                  ) : (
                    <>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        placeholder="Min"
                        value={amounts.min ?? ''}
                        onChange={(e) => onChildGoalAmountChange(child.habitId, { min: e.target.value })}
                        sx={{
                          width: 80,
                          '& .MuiOutlinedInput-root': inputRootSx,
                          '& .MuiOutlinedInput-input': { color: '#ffffff' },
                        }}
                      />
                      <Typography variant="body2" sx={{ color: '#ffffff' }}> – </Typography>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        placeholder="Max"
                        value={amounts.max ?? ''}
                        onChange={(e) => onChildGoalAmountChange(child.habitId, { max: e.target.value })}
                        sx={{
                          width: 80,
                          '& .MuiOutlinedInput-root': inputRootSx,
                          '& .MuiOutlinedInput-input': { color: '#ffffff' },
                        }}
                      />
                    </>
                  )}
                  <Typography variant="body2" sx={{ color: '#aaa' }}>
                    {showIntervalUnitControls ? `${goalUnit} per ${intervalUnit}` : goalUnit}
                  </Typography>
                </Box>
              </Box>
            );
          })}

      {childValidationError && (
        <Alert severity="error" sx={{ mb: 2 }}>{childValidationError}</Alert>
      )}

      {childHabits.length > 0 && parentHabitDisplayName && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: '#32CA1D', fontWeight: 700 }}>
            {parentHabitDisplayName} Goal
          </Typography>
        </Box>
      )}

      {selectedGoalType === 'THRESHOLD' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: '#32CA1D' }}><strong>Goal:</strong></Typography>
          {parentAmountsAsText ? (
            <Typography variant="body2" sx={{ color: '#ffffff' }}>
              {thresholdValue || '0'}
            </Typography>
          ) : (
            <TextField
              size="small"
              type="number"
              inputProps={{ step: '0.01', min: '0.01' }}
              value={thresholdValue}
              onChange={(e) => onThresholdValueChange(e.target.value)}
              sx={{
                width: 100,
                '& .MuiOutlinedInput-root': inputRootSx,
                '& .MuiOutlinedInput-input': { color: '#ffffff' },
              }}
            />
          )}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={goalUnit}
              onChange={(e) => onGoalUnitChange(e.target.value)}
              displayEmpty
              renderValue={(v) => v || <em style={{ color: 'rgba(255,255,255,0.5)' }}>Select unit</em>}
              sx={selectSx}
            >
              {availableGoalUnits.map((unit) => (
                <MenuItem key={unit} value={unit}>{unit}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {showIntervalUnitControls && (
            <>
              <Typography variant="body2" sx={{ color: '#ffffff' }}>per</Typography>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={intervalUnit}
                  onChange={(e) => onIntervalUnitChange(e.target.value)}
                  displayEmpty
                  renderValue={(v) => v || <em style={{ color: 'rgba(255,255,255,0.5)' }}>Select interval</em>}
                  sx={selectSx}
                >
                  {availableIntervalUnits.map((unit) => (
                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          <Tooltip title="Threshold: A minimum target to reach or exceed" arrow placement="top">
            <InfoIcon sx={{ fontSize: '1rem', color: 'rgba(50, 202, 29, 0.83)', cursor: 'help' }} />
          </Tooltip>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: '#32CA1D' }}><strong>Goal:</strong></Typography>
          {parentAmountsAsText ? (
            <Typography variant="body2" sx={{ color: '#ffffff' }}>
              {rangeMin || '0'}
            </Typography>
          ) : (
            <TextField
              size="small"
              type="number"
              inputProps={{ step: '0.01' }}
              value={rangeMin}
              placeholder="Min"
              onChange={(e) => onRangeMinChange(e.target.value)}
              sx={{
                width: 80,
                '& .MuiOutlinedInput-root': inputRootSx,
                '& .MuiOutlinedInput-input': { color: '#ffffff' },
              }}
            />
          )}
          <Typography variant="body2" sx={{ color: '#ffffff' }}> – </Typography>
          {parentAmountsAsText ? (
            <Typography variant="body2" sx={{ color: '#ffffff' }}>
              {rangeMax || '0'}
            </Typography>
          ) : (
            <TextField
              size="small"
              type="number"
              inputProps={{ step: '0.01' }}
              value={rangeMax}
              placeholder="Max"
              onChange={(e) => onRangeMaxChange(e.target.value)}
              sx={{
                width: 80,
                '& .MuiOutlinedInput-root': inputRootSx,
                '& .MuiOutlinedInput-input': { color: '#ffffff' },
              }}
            />
          )}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={goalUnit}
              onChange={(e) => onGoalUnitChange(e.target.value)}
              displayEmpty
              renderValue={(v) => v || <em style={{ color: 'rgba(255,255,255,0.5)' }}>Select unit</em>}
              sx={selectSx}
            >
              {availableGoalUnits.map((unit) => (
                <MenuItem key={unit} value={unit}>{unit}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {showIntervalUnitControls && (
            <>
              <Typography variant="body2" sx={{ color: '#ffffff' }}>per</Typography>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={intervalUnit}
                  onChange={(e) => onIntervalUnitChange(e.target.value)}
                  displayEmpty
                  renderValue={(v) => v || <em style={{ color: 'rgba(255,255,255,0.5)' }}>Select interval</em>}
                  sx={selectSx}
                >
                  {availableIntervalUnits.map((unit) => (
                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          <Tooltip title={selectedGoalType === 'AVERAGE' ? 'Average: Keep the average amount within a minimum and maximum range' : 'Range: A target range with minimum and maximum values'} arrow placement="top">
            <InfoIcon sx={{ fontSize: '1rem', color: 'rgba(50, 202, 29, 0.83)', cursor: 'help' }} />
          </Tooltip>
        </Box>
      )}

      {goalTypeSelectorPlacement === 'bottom' && goalTypeSelector}
    </>
  );
};

export default ParentChildGoalEditor;
